import gql from 'graphql-tag';
import I18n from 'i18n-js';
import React from 'react';

import CheckSvg from '@reverbdotcom/commons/design-system/icons/core/check.svg?component';
import { RCTextWithIcon } from '@reverbdotcom/commons/src/rc/RCTextWithIcon';

import { SellFormKeyValueList } from './form/components/SellFormKeyValueList';
import { MyShippingProfilePrices } from '../../schemas/rql';

enum PriceField {
  STANDARD = 'rate',
  COMBINED = 'incrementalRate',
  EXPEDITED = 'expeditedRate',
}

export const MyShippingProfilePricesFragment = gql`
  fragment MyShippingProfilePrices on rql_Me {
    uuid
    shop {
      _id
      shippingProfiles {
        id
        name
        localPickup
        shippingRates {
          regionCode
          regionName
          rate {
            display
          }
          incrementalRate {
            display
          }
          expeditedRate {
            display
          }
        }
      }
    }
  }
`;

interface IProps {
  shippingProfile: MyShippingProfilePrices.ShippingProfiles,
}

function ShippingRateSummary({ shippingRate }: { shippingRate: MyShippingProfilePrices.ShippingRates }) {
  const standardShippingOnly = shippingRate.rate?.display && !shippingRate.incrementalRate?.display && !shippingRate.expeditedRate?.display;

  return (
    <>
      {standardShippingOnly && <div>{shippingRate.rate.display}</div>}

      {!standardShippingOnly && (
        <>
          {[PriceField.STANDARD, PriceField.COMBINED, PriceField.EXPEDITED].map((type) => (
            shippingRate[type]?.display && (
              <div key={type}>
                <span>
                  <strong>
                    {I18n.t(`discovery.sellForm.shippingSection.profileCard.rateType.${type}`)}
                  </strong>
                </span>
                <span>
                  {shippingRate[type]?.display}
                </span>
              </div>
            )
          ))}
        </>
      )}
    </>
  );
}

export default function ShippingProfileSummaryTable({ shippingProfile }: IProps) {
  const offersShipping = shippingProfile.shippingRates.length > 0;
  const offersLocalPickup = shippingProfile.localPickup;

  const displayShippingMethods = [
    offersShipping && I18n.t('discovery.sellForm.shippingSection.deliveryOptions.shipping'),
    offersLocalPickup && I18n.t('discovery.sellForm.shippingSection.deliveryOptions.localPickup'),
  ].filter(v => v);

  return (
    <div className="padding-1">
      <div data-delivery-options>
        <strong>
          {I18n.t('discovery.sellForm.shippingSection.deliveryOptionsHeader')}
        </strong>
        {displayShippingMethods.map((method) => (
          <div key={method} className="mt-1">
            <RCTextWithIcon svgComponent={CheckSvg} placement="left">
              {method}
            </RCTextWithIcon>
          </div>
        ))}
      </div>

      {offersShipping && (
        <div className="bg-white" data-shipping-rates-list>
          <div className="mt-3">
            <strong>
              {I18n.t('discovery.sellForm.shippingSection.profileCard.costsPerRegion')}
            </strong>
          </div>
          <SellFormKeyValueList>
            {shippingProfile.shippingRates.map((sr) => (
              <SellFormKeyValueList.Row label={sr.regionName} key={sr.regionCode}>
                <ShippingRateSummary shippingRate={sr} />
              </SellFormKeyValueList.Row>
            ))}
          </SellFormKeyValueList>
        </div>
      )}
    </div>
  );
}
